import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { getSupabase, isSupabaseConfigured } from '../lib/supabaseClient';
import { resolveWorkspaceSeedFromAnon } from '../data/journeyBridge';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const ANON_BRIEF_KEY = 'bp.anonymousBrief';

/**
 * Carry the ANONYMOUS marketing journey (5-stage + project, in
 * localStorage) into the logged-in scenario on first sign-in, so the
 * visitor lands on the exact project + stage they were exploring.
 *
 * Guarded so it ONLY seeds a fresh account: if the user has already
 * onboarded (or has any build_type / building_stage), we leave their
 * scenario untouched - already-logged-in users behave as per normal.
 * Best-effort and silent; the two systems stay independent.
 */
const _seedJourneyFromAnonymous = async (userId, fetchProfile) => {
  if (typeof window === 'undefined' || !userId) return;
  let seed;
  try { seed = resolveWorkspaceSeedFromAnon(); } catch { return; }
  if (!seed) return;
  try {
    const supabase = getSupabase();
    const { data: prof } = await supabase
      .from('profiles')
      .select('id, onboarding_completed, building_stage, build_type')
      .eq('id', userId)
      .maybeSingle();
    if (prof && (prof.onboarding_completed || prof.building_stage || prof.build_type)) return;
    await supabase.from('profiles').upsert(
      {
        id: userId,
        build_type: seed.build_type,
        building_stage: seed.building_stage,
        onboarding_situation: seed.building_stage,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'id' }
    );
    if (fetchProfile) fetchProfile(userId);
  } catch {
    /* silent - seeding is best-effort */
  }
};

/**
 * Push the anonymous brief stored in localStorage to /api/user-brief/sync
 * and clear it on success. Silent on failure.
 */
const _syncAnonymousBrief = async (accessToken) => {
  if (typeof window === 'undefined' || !accessToken || !API_BASE) return;
  let payload;
  try {
    const raw = window.localStorage.getItem(ANON_BRIEF_KEY);
    if (!raw) return;
    payload = JSON.parse(raw);
    if (!payload || typeof payload !== 'object' || !payload.updatedAt) return;
  } catch {
    return;
  }
  try {
    const res = await fetch(`${API_BASE}/api/user-brief/sync`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        state: payload.state,
        region: payload.region,
        suburb: payload.suburb,
        build_type: payload.build_type,
        build_goal: payload.build_goal,
        budget: payload.budget,
        budget_band: payload.budget_band,
        bedrooms: payload.bedrooms,
        timing: payload.timing,
        priorities: Array.isArray(payload.priorities) ? payload.priorities : [],
        block_width: payload.block_width,
        extras: payload.extras || {},
      }),
    });
    if (res.ok) {
      try { window.localStorage.removeItem(ANON_BRIEF_KEY); } catch { /* ignore */ }
      try { window.dispatchEvent(new CustomEvent('buildpilot:anonymous-brief-synced')); } catch { /* ignore */ }
    }
  } catch {
    /* network blip - keep the brief in localStorage for next sign-in */
  }
};

const AuthContext = createContext({
  user: null,
  session: null,
  loading: true,
  signUp: async () => ({}),
  signIn: async () => ({}),
  signOut: async () => ({}),
  resetPassword: async () => ({}),
  signInWithOAuth: async () => ({}),
  configured: false,
});

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const configured = isSupabaseConfigured();

  const fetchProfile = useCallback(async (uid) => {
    if (!uid) { setProfile(null); return; }
    try {
      const supabase = getSupabase();
      const { data } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', uid)
        .maybeSingle();
      setProfile(data || null);
    } catch (e) {
      setProfile(null);
    }
  }, []);

  useEffect(() => {
    if (!configured) {
      setLoading(false);
      return undefined;
    }
    const supabase = getSupabase();

    // Initial session hydration
    supabase.auth.getSession().then(({ data }) => {
      const s = data.session ?? null;
      setSession(s);
      setUser(s?.user ?? null);
      setLoading(false);
      if (s?.user?.id) {
        fetchProfile(s.user.id);
        if (s.access_token) _syncAnonymousBrief(s.access_token);
        _seedJourneyFromAnonymous(s.user.id, fetchProfile);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
      setUser(newSession?.user ?? null);
      fetchProfile(newSession?.user?.id);
      // On a fresh SIGNED_IN, push any anonymous brief in localStorage
      // to the server then clear it. Errors are silent - losing an
      // unauthed brief is a soft failure.
      if (_event === 'SIGNED_IN' && newSession?.access_token) {
        _syncAnonymousBrief(newSession.access_token);
        _seedJourneyFromAnonymous(newSession.user?.id, fetchProfile);
        // 2026-02-15 Slice B - back-fill user_id on every previous
        // marketing_captures + user_events row for this email so the
        // user's pre-signup history lands on their new profile.
        try {
          // eslint-disable-next-line global-require
          const { claimAnonymousHistory } = require('../../lib/trackEvent');
          claimAnonymousHistory(newSession.user);
        } catch { /* swallow */ }
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [configured, fetchProfile]);

  // Keep the in-memory profile + auth.user in lockstep with the
  // database for the signed-in user. Any change to
  // `public.profiles.building_stage`, `profile.build_type`, etc., or
  // to `auth.users.user_metadata.build_type_stages` (where the
  // dashboard BuildTrack timeline persists per-tab stages), made
  // from ANYWHERE (iOS app, workspace Profile / Settings /
  // BuildProgress page, another browser tab) propagates to every
  // component reading `useAuth().profile` or `useAuth().user`.
  //
  // Three complementary mechanisms:
  //   1. Supabase `postgres_changes` realtime push on `profiles` , 
  //      instant when the table is in the `supabase_realtime`
  //      publication; silent no-op otherwise.
  //   2. Window focus + visibilitychange, refetches the profile
  //      AND `supabase.auth.getUser()` (which pulls fresh
  //      user_metadata) so cross-device updates land within a few
  //      seconds of tab focus.
  //   3. 30s background poll while the tab is visible, guarantees
  //      iOS edits reach desktop even without realtime + without a
  //      focus event.
  useEffect(() => {
    if (!configured || !user?.id) return undefined;
    const supabase = getSupabase();
    const uid = user.id;

    const refreshAll = async () => {
      // Refresh user_metadata (lives on auth.users, NOT in `profiles`
      //, so the postgres_changes channel below doesn't see it).
      try {
        const { data } = await supabase.auth.getUser();
        if (data?.user) setUser(data.user);
      } catch { /* offline / network blip, silent */ }
      // Refresh the profiles row.
      fetchProfile(uid);
    };

    // 1) Realtime channel for this user's own profile row.
    const channel = supabase
      .channel(`bp-profile-${uid}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'profiles', filter: `id=eq.${uid}` },
        () => { fetchProfile(uid); }
      )
      .subscribe();

    // 2) Refetch when the tab regains focus / becomes visible.
    const onFocus = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        refreshAll();
      }
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);

    // 3) Periodic poll (matches the Checklists pattern, covers cases
    //    where realtime isn't enabled for the table and the user
    //    leaves the tab in the foreground without it ever losing focus).
    const pollId = window.setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        refreshAll();
      }
    }, 30000);

    return () => {
      supabase.removeChannel(channel);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
      window.clearInterval(pollId);
    };
  }, [configured, user?.id, fetchProfile]);

  const refreshProfile = useCallback(() => fetchProfile(user?.id), [user?.id, fetchProfile]);

  const signUp = useCallback(async (email, password, metadata = {}) => {
    if (!configured) return { error: { message: 'Auth not configured' } };
    const supabase = getSupabase();
    return supabase.auth.signUp({ email, password, options: { data: metadata } });
  }, [configured]);

  const signIn = useCallback(async (email, password) => {
    if (!configured) return { error: { message: 'Auth not configured' } };
    const supabase = getSupabase();
    return supabase.auth.signInWithPassword({ email, password });
  }, [configured]);

  const signOut = useCallback(async () => {
    if (!configured) return {};
    const supabase = getSupabase();
    return supabase.auth.signOut();
  }, [configured]);

  const resetPassword = useCallback(async (email) => {
    if (!configured) return { error: { message: 'Auth not configured' } };
    const supabase = getSupabase();
    const redirectTo = `${window.location.origin}/workspace/reset-password`;
    return supabase.auth.resetPasswordForEmail(email, { redirectTo });
  }, [configured]);

  /**
   * OAuth sign-in (Google, Apple, etc).
   * Redirects the browser to the provider, then back to /workspace/auth/callback
   * which finalises the session and routes the user onward.
   */
  const signInWithOAuth = useCallback(async (provider) => {
    if (!configured) return { error: { message: 'Auth not configured' } };
    const supabase = getSupabase();
    const redirectTo = `${window.location.origin}/workspace/auth/callback`;
    return supabase.auth.signInWithOAuth({
      provider, // 'google' | 'apple'
      options: {
        redirectTo,
        queryParams: provider === 'google'
          ? { access_type: 'offline', prompt: 'consent' }
          : undefined,
      },
    });
  }, [configured]);

  return (
    <AuthContext.Provider value={{ user, session, profile, refreshProfile, loading, signUp, signIn, signOut, resetPassword, signInWithOAuth, configured }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
